// Utility functions for table operations: search, filter, sort

// Global search across all fields in a row
export const searchData = <T>(data: T[], searchTerm: string): T[] => {
  if (!searchTerm || searchTerm.trim() === '') return data;

  const term = searchTerm.toLowerCase().trim();

  return data.filter((row) => {
    // Convert all values to strings and search
    return Object.values(row as any).some((value) => {
      if (value === null || value === undefined) return false;

      // Handle nested objects (like profile, offerDetails)
      if (typeof value === 'object') {
        return Object.values(value as any).some((nestedVal) =>
          String(nestedVal).toLowerCase().includes(term)
        );
      }

      return String(value).toLowerCase().includes(term);
    });
  });
};

// Filter data by multiple criteria
export const filterData = <T>(data: T[], filters: Record<string, any>): T[] => {
  let filteredData = [...data];

  // Filter by month
  if (filters.month && filters.month !== 'all') {
    filteredData = filteredData.filter((row: any) => row.hired_month === filters.month);
  }

  // Filter by POC
  if (filters.poc && filters.poc !== 'all') {
    filteredData = filteredData.filter((row: any) => row.poc_name === filters.poc);
  }



  // Filter by invoice generated (for invoices table) (Case Insensitive)
  if (filters.status && filters.status !== 'all') {
    filteredData = filteredData.filter((row: any) =>
      String(row.status).toLowerCase() === filters.status.toLowerCase()
    );
  }

  return filteredData;
};

// Sort data by column
export const sortData = <T>(data: T[], sortConfig: { key: string | null; direction: 'asc' | 'desc' }): T[] => {
  if (!sortConfig.key) return data;

  const sortedData = [...data];

  sortedData.sort((a: any, b: any) => {
    let aVal = a[sortConfig.key!];
    let bVal = b[sortConfig.key!];

    // Handle null/undefined
    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;

    // Handle numbers (including fee amounts)
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    }

    // Handle dates
    if (sortConfig.key!.toLowerCase().includes('date')) {
      const dateA = new Date(aVal).getTime();
      const dateB = new Date(bVal).getTime();
      return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
    }

    // Handle strings
    const strA = String(aVal).toLowerCase();
    const strB = String(bVal).toLowerCase();

    if (strA < strB) return sortConfig.direction === 'asc' ? -1 : 1;
    if (strA > strB) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  return sortedData;
};

// Paginate data
export const paginateData = <T>(data: T[], currentPage: number, itemsPerPage: number): T[] => {
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  return data.slice(startIndex, endIndex);
};

// Calculate total pages
export const getTotalPages = (totalItems: number, itemsPerPage: number): number => {
  return Math.ceil(totalItems / itemsPerPage);
};

// Format currency
export const formatCurrency = (amount: number | string): string => {
  return `₹${Number(amount).toLocaleString('en-IN')}`;
};

// Format date
export const formatDate = (dateString: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
};

// Debounce function for search
export const debounce = (func: (...args: any[]) => void, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};
